// Import label dimension values from SCSS
// eslint-disable-next-line import/no-unresolved
import labelStyles from '../MapLabels/_MapLabelsShared.scss';

// Parse pixel values from SCSS exports (they come as strings like "20px")
const parsePixelValue = (value) => parseInt(value, 10) || 0;

// Label dimensions from CSS
const LABEL_FONT_SIZE = parsePixelValue(labelStyles.labelFontSize);
const LABEL_LINE_HEIGHT = parseFloat(labelStyles.labelLineHeight) || 1.3;
const LABEL_PADDING_V = parsePixelValue(labelStyles.labelPaddingVertical);
const LABEL_PADDING_H = parsePixelValue(labelStyles.labelPaddingHorizontal);
const LABEL_CHAR_WIDTH = parsePixelValue(labelStyles.labelCharWidth);
const LABEL_MAX_WIDTH = parsePixelValue(labelStyles.labelMaxWidth);
const CLUSTER_ICON_SIZE = parsePixelValue(labelStyles.clusterIconSize);
const CLUSTER_GAP = parsePixelValue(labelStyles.clusterGap);

// Link label elements (divider and plus icon)
const LINK_DIVIDER_WIDTH = parsePixelValue(labelStyles.linkDividerWidth);
const LINK_DIVIDER_MARGIN = parsePixelValue(labelStyles.linkDividerMargin);
const LINK_PLUS_ICON_SIZE = parsePixelValue(labelStyles.linkPlusIconSize);

// Marker dimensions
const MARKER_DOT_SIZE = parsePixelValue(labelStyles.markerDotSize);
const MARKER_LABEL_GAP = parsePixelValue(labelStyles.markerLabelGap);

// Clustering aggressiveness - higher = larger bounds = more clustering
const CLUSTERING_AGGRESSIVENESS = 1;

// Debug mode - set to true to show bounding boxes
export const CLUSTERING_DEBUG = false;

// Debug mode - set to true to disable actual clustering (shows all labels unclustered)
export const CLUSTERING_DISABLED_FOR_DEBUG = false;

/**
 * Get the center coordinates for a place label
 * Center is pre-computed in Map.php for all shape types
 * @param {Object} place - Place label data
 * @returns {Object} - {x, y} coordinates
 */
export const getPlaceCenter = (place) => {
  const { center } = place || {};
  return center ? { x: center.x, y: center.y } : { x: 0, y: 0 };
};

/**
 * Estimate the bounding box for a place label in percentage space (0-100)
 * HTML labels have constant pixel size, converted to % using actual container size
 * Bounds scale with zoom - at higher zoom, labels take up less % of screen
 * @param {Object} place - Place label data
 * @param {Object} center - Center coordinates as percentages {x, y} (0-100)
 * @param {number} zoomScale - Scale factor (1 = base zoom, 0.5 = 1 level higher)
 * @param {Object} viewBox - SVG viewBox for center calculations
 * @param {Object} containerSize - Actual container dimensions for pixel-to-% conversion
 * @returns {Object} - Bounding box {x, y, width, height} in percentage space
 */
export const estimateLabelBounds = (place, center, zoomScale = 1, viewBox = null, containerSize = null) => {
  const { pin } = place || {};
  const {
    label_text = '',
    label_orientation = 'left',
    create_modal,
    wrap_label = true,
  } = pin || {};

  // Use container dimensions for pixel-to-percentage conversion (labels are fixed pixel size)
  // Fall back to viewBox if container size not available yet
  const refWidth = containerSize?.width || viewBox?.width || 1000;
  const refHeight = containerSize?.height || viewBox?.height || 1000;

  // Label dimensions from CSS
  // For link labels (create_modal), add width for divider + plus icon
  const dividerTotalWidth = LINK_DIVIDER_WIDTH + (LINK_DIVIDER_MARGIN * 2);
  const linkLabelExtraPx = create_modal ? (dividerTotalWidth + LINK_PLUS_ICON_SIZE) : 0;

  // Calculate max text width available (max-width minus padding and link extras)
  const maxTextWidth = LABEL_MAX_WIDTH - (LABEL_PADDING_H * 2) - linkLabelExtraPx;

  // Simulate CSS word-wrapping to estimate lines and actual width.
  // When wrap_label is false, the label renders on a single line with no
  // max-width, so all words go on one line regardless of total width.
  const words = label_text.split(' ');
  const lines = [];
  let currentLine = '';
  let currentLineWidth = 0;

  words.forEach((word) => {
    const wordWidth = word.length * LABEL_CHAR_WIDTH;
    const spaceWidth = currentLine ? LABEL_CHAR_WIDTH : 0; // Space before word

    if (!wrap_label || currentLineWidth + spaceWidth + wordWidth <= maxTextWidth) {
      // Word fits on current line (or wrapping disabled)
      currentLine = currentLine ? `${currentLine} ${word}` : word;
      currentLineWidth += spaceWidth + wordWidth;
    } else {
      // Word doesn't fit - start new line
      if (currentLine) {
        lines.push({ text: currentLine, width: currentLineWidth });
      }
      currentLine = word;
      currentLineWidth = wordWidth;
    }
  });
  // Don't forget the last line
  if (currentLine) {
    lines.push({ text: currentLine, width: currentLineWidth });
  }

  const numberOfLines = lines.length || 1;
  const maxLineWidth = Math.max(...lines.map((l) => l.width), 20);

  // Width: use the widest line plus padding and link extras
  const labelWidthPx = maxLineWidth + (LABEL_PADDING_H * 2) + linkLabelExtraPx;

  // Height increases with number of lines
  const singleLineHeight = LABEL_FONT_SIZE * LABEL_LINE_HEIGHT;
  const textHeightPx = singleLineHeight * numberOfLines;
  const labelHeightPx = textHeightPx + (LABEL_PADDING_V * 2);

  // Convert pixel dimensions to percentage of container
  // Labels are fixed pixel size, so use container dimensions for accurate %
  const estimatedWidth = (labelWidthPx / refWidth) * 100 * zoomScale * CLUSTERING_AGGRESSIVENESS;
  const estimatedHeight = (labelHeightPx / refHeight) * 100 * zoomScale * CLUSTERING_AGGRESSIVENESS;

  // Pin and offset dimensions as percentages (using CSS values)
  const markerHalfWidth = MARKER_DOT_SIZE / 2;
  const totalOffset = markerHalfWidth + MARKER_LABEL_GAP;

  const pinRadius = (totalOffset / refWidth) * 100 * zoomScale * CLUSTERING_AGGRESSIVENESS;
  const labelOffset = 0; // Included in pinRadius now
  const horizontalPadding = 0;
  const verticalPadding = 0;

  // Calculate position based on orientation
  let x;
  let y;

  switch (label_orientation) {
    case 'left':
      // Label is to the left of the pin
      x = center.x - pinRadius - labelOffset - estimatedWidth - horizontalPadding;
      y = center.y - estimatedHeight / 2;
      break;
    case 'right':
      // Label is to the right of the pin
      x = center.x + pinRadius + labelOffset;
      y = center.y - estimatedHeight / 2;
      break;
    case 'top':
      // Label is above the pin
      x = center.x - estimatedWidth / 2;
      y = center.y - pinRadius - verticalPadding - labelOffset - estimatedHeight;
      break;
    case 'bottom':
      // Label is below the pin
      x = center.x - estimatedWidth / 2;
      y = center.y + pinRadius + verticalPadding + labelOffset;
      break;
    default:
      // Default to left
      x = center.x - pinRadius - labelOffset - estimatedWidth - horizontalPadding;
      y = center.y - estimatedHeight / 2;
      break;
  }

  return {
    x,
    y,
    width: estimatedWidth,
    height: estimatedHeight,
    centerX: center.x,
    centerY: center.y,
    // Include pixel dimensions for debug visualization
    widthPx: labelWidthPx,
    heightPx: labelHeightPx,
  };
};

/**
 * Check if two bounding boxes overlap
 * @param {Object} box1 - First bounding box
 * @param {Object} box2 - Second bounding box
 * @returns {boolean} - True if boxes overlap
 */
export const boxesOverlap = (box1, box2) => !(
  box1.x + box1.width < box2.x
    || box2.x + box2.width < box1.x
    || box1.y + box1.height < box2.y
    || box2.y + box2.height < box1.y
);

/**
 * Compute the area of the rectangular intersection of two boxes.
 * Returns 0 when they don't overlap.
 */
export const overlapArea = (a, b) => {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  if (w <= 0 || h <= 0) return 0;
  return w * h;
};

/**
 * Calculate the center point of multiple labels from their bounds
 * @param {Array} labelBoundsArray - Array of label bounding boxes (each has centerX, centerY)
 * @returns {Object} - Center coordinates {x, y} in percentage space
 */
export const calculateClusterCenter = (labelBoundsArray) => {
  if (!labelBoundsArray || labelBoundsArray.length === 0) {
    return { x: 0, y: 0 };
  }

  const sum = labelBoundsArray.reduce((acc, bounds) => ({
    x: acc.x + bounds.centerX,
    y: acc.y + bounds.centerY,
  }), { x: 0, y: 0 });

  return {
    x: sum.x / labelBoundsArray.length,
    y: sum.y / labelBoundsArray.length,
  };
};

/**
 * Calculate the bounding box of a cluster (union of all label bounds)
 * @param {Array} labelBounds - Array of label bounding boxes
 * @returns {Object} - Cluster bounding box {x, y, width, height}
 */
export const calculateClusterBounds = (labelBounds) => {
  if (!labelBounds || labelBounds.length === 0) {
    return {
      x: 0, y: 0, width: 0, height: 0,
    };
  }

  // Find the min/max coordinates across all labels
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  labelBounds.forEach((bounds) => {
    minX = Math.min(minX, bounds.x);
    minY = Math.min(minY, bounds.y);
    maxX = Math.max(maxX, bounds.x + bounds.width);
    maxY = Math.max(maxY, bounds.y + bounds.height);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Estimate the bounding box for a cluster badge in percentage space (0-100)
 * Cluster badges are taller than regular labels (count rectangle + icon circle)
 * @param {Object} clusterCenter - Cluster center coordinates as percentages {x, y}
 * @param {number} count - Number of labels in cluster (for width calculation)
 * @param {number} zoomScale - Scale factor based on zoom
 * @param {Object} viewBox - viewBox (fallback when containerSize is unavailable)
 * @param {Object} containerSize - Actual container dimensions for px-to-% conversion
 * @returns {Object} - Cluster badge bounding box {x, y, width, height} in percentage space
 */
export const estimateClusterBadgeBounds = (
  clusterCenter, count, zoomScale = 1, viewBox = null, containerSize = null,
) => {
  // Use container dimensions for the percentage conversion so the badge bounds
  // sit in the same screen-space coordinate system as label bounds. Falling
  // back to viewBox alone (which the original implementation did) made the
  // badge appear up to 2× its true screen size when viewBox.width <
  // container.width — Phase 2 then absorbed labels well outside the actual
  // badge footprint.
  const refWidth = containerSize?.width || viewBox?.width || 1000;
  const refHeight = containerSize?.height || viewBox?.height || 1000;

  // Badge dimensions from CSS
  // The badge has a text label ("X More") plus a circle icon below
  const textContent = `${count} More`;
  const textChars = textContent.length;
  const rectWidthPx = (textChars * LABEL_CHAR_WIDTH) + (LABEL_PADDING_H * 2);
  const textHeightPx = LABEL_FONT_SIZE * LABEL_LINE_HEIGHT;
  const rectHeightPx = textHeightPx + (LABEL_PADDING_V * 2);

  const totalWidth = (rectWidthPx / refWidth) * 100 * zoomScale * CLUSTERING_AGGRESSIVENESS;
  const badgeHeightPx = rectHeightPx + CLUSTER_GAP + CLUSTER_ICON_SIZE;
  const totalHeight = (badgeHeightPx / refHeight) * 100 * zoomScale * CLUSTERING_AGGRESSIVENESS;

  // Center the bounding box on the cluster center
  return {
    x: clusterCenter.x - totalWidth / 2,
    y: clusterCenter.y - totalHeight / 2,
    width: totalWidth,
    height: totalHeight,
  };
};

/**
 * Spatial grid for efficient overlap detection
 * Divides space into grid cells to reduce overlap checks from O(n²) to O(n)
 */
class SpatialGrid {
  constructor(cellSize = 100) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }

  // Get grid cell key for a position
  getCellKey(x, y) {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  // Get all cell keys that a bounding box overlaps
  getCellKeysForBounds(bounds) {
    const minCellX = Math.floor(bounds.x / this.cellSize);
    const maxCellX = Math.floor((bounds.x + bounds.width) / this.cellSize);
    const minCellY = Math.floor(bounds.y / this.cellSize);
    const maxCellY = Math.floor((bounds.y + bounds.height) / this.cellSize);

    const keys = [];
    for (let x = minCellX; x <= maxCellX; x++) {
      for (let y = minCellY; y <= maxCellY; y++) {
        keys.push(`${x},${y}`);
      }
    }
    return keys;
  }

  // Add a label to the grid
  add(label) {
    const keys = this.getCellKeysForBounds(label.bounds);
    keys.forEach((key) => {
      if (!this.grid.has(key)) {
        this.grid.set(key, []);
      }
      this.grid.get(key).push(label);
    });
  }

  // Get nearby labels that could potentially overlap
  getNearby(bounds) {
    const keys = this.getCellKeysForBounds(bounds);
    const nearby = new Set();
    keys.forEach((key) => {
      const labels = this.grid.get(key);
      if (labels) {
        labels.forEach((label) => nearby.add(label));
      }
    });
    return Array.from(nearby);
  }
}

/**
 * Convert SVG coordinates to percentage space (0-100)
 * @param {Object} svgCoord - {x, y} in SVG coordinate space
 * @param {Object} viewBox - SVG viewBox {x, y, width, height}
 * @returns {Object} - {x, y} as percentages (0-100)
 */
export const svgToPercent = (svgCoord, viewBox) => {
  if (!svgCoord || !viewBox) return { x: 0, y: 0 };
  const vbX = viewBox.x || 0;
  const vbY = viewBox.y || 0;
  const vbWidth = viewBox.width || 1000;
  const vbHeight = viewBox.height || 1000;
  return {
    x: ((svgCoord.x - vbX) / vbWidth) * 100,
    y: ((svgCoord.y - vbY) / vbHeight) * 100,
  };
};

/**
 * Cluster overlapping place labels
 * Works in percentage space (0-100) for consistent bounds regardless of SVG size
 * @param {Array} places - Array of place label data
 * @param {Object} viewBox - SVG viewBox for converting centers to percentages
 * @param {number} zoomScale - Scale factor based on zoom (1 = base, 0.5 = zoomed in 1 level)
 * @param {boolean} enableProfiling - Enable performance profiling (default false)
 * @param {Object} containerSize - Actual container dimensions for px to % conversion
 * @returns {Object} - {clusters: Array, unclustered: Array}
 */
export const clusterOverlappingLabels = (places, viewBox, zoomScale = 1, enableProfiling = false, containerSize = null) => {
  const profiler = enableProfiling ? {
    start: () => performance.now(),
    end: (label, startTime) => {
      const duration = performance.now() - startTime;
      // eslint-disable-next-line no-console
      return duration;
    },
  } : {
    start: () => 0,
    end: () => 0,
  };

  const totalStart = profiler.start('Total clustering');

  if (!places || places.length === 0) {
    return { clusters: [], unclustered: [] };
  }

  // Calculate centers (in percentage space) and bounds for all labels
  // Store both SVG center (for output) and percentage center (for overlap detection)
  const boundsStart = profiler.start('Phase 0: Calculate bounds');
  const labelsWithBounds = places.map((place) => {
    const svgCenter = getPlaceCenter(place);
    const pctCenter = svgToPercent(svgCenter, viewBox);
    const bounds = estimateLabelBounds(place, pctCenter, zoomScale, viewBox, containerSize);
    return {
      place,
      svgCenter,
      pctCenter,
      bounds,
      clustered: false,
    };
  });
  profiler.end('Phase 0: Calculate bounds', boundsStart);

  // Debug mode: skip clustering but return bounds for visualization
  if (CLUSTERING_DISABLED_FOR_DEBUG) {
    const debugBounds = CLUSTERING_DEBUG ? labelsWithBounds.map((label) => ({
      uuid: label.place?.pin?.uuid,
      labelText: label.place?.pin?.label_text,
      orientation: label.place?.pin?.label_orientation || 'right',
      bounds: label.bounds,
      clustered: false,
    })) : null;

    return {
      clusters: [],
      unclustered: places,
      debugBounds,
    };
  }

  const clusters = [];

  // Phase 1: Initial greedy clustering using spatial grid - O(n) average case
  const phase1Start = profiler.start('Phase 1: Initial clustering (spatial grid)');

  // Build spatial grid for fast proximity queries
  // Larger cell size = coarser grid = fewer cells to check
  const gridBuildStart = profiler.start('  Grid build');
  const grid = new SpatialGrid(15);
  labelsWithBounds.forEach((label) => grid.add(label));
  profiler.end('  Grid build', gridBuildStart);

  const clusteringStart = profiler.start('  Clustering');
  const computeOverlapRatio = (bounds1, bounds2) => {
    if (!boxesOverlap(bounds1, bounds2)) return 0;
    const area1 = bounds1.width * bounds1.height;
    const area2 = bounds2.width * bounds2.height;
    const minArea = Math.min(area1, area2);
    const intersection = overlapArea(bounds1, bounds2);
    return minArea > 0 ? intersection / minArea : 0;
  };

  labelsWithBounds.forEach((label) => {
    if (label.clustered) {
      return;
    }

    // Use spatial grid to find only nearby labels (O(1) average case)
    const nearby = grid.getNearby(label.bounds);
    const overlapping = [label];

    nearby.forEach((other) => {
      if (other.clustered || other === label) {
        return;
      }

      // Check if "other" overlaps with ANY label already in cluster (connected-components)
      // Prevents hub-and-spoke clustering where labels far apart are joined only via hub
      const overlapsWithCluster = overlapping.some((clusterMember) => {
        const overlapRatio = computeOverlapRatio(clusterMember.bounds, other.bounds);
        return overlapRatio >= 0.20;
      });

      if (overlapsWithCluster) {
        overlapping.push(other);
        other.clustered = true;
      }
    });

    // If we found overlapping labels, create a cluster
    if (overlapping.length > 1) {
      label.clustered = true;
      const clusterLabels = overlapping.map((l) => l.place);
      const clusterLabelBounds = overlapping.map((l) => l.bounds);
      const clusterSvgCenters = overlapping.map((l) => l.svgCenter);
      const clusterPctCenter = calculateClusterCenter(clusterLabelBounds);

      // Calculate SVG center (average) for rendering output
      const svgCenterSum = clusterSvgCenters.reduce(
        (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
        { x: 0, y: 0 },
      );
      const clusterSvgCenter = {
        x: svgCenterSum.x / clusterSvgCenters.length,
        y: svgCenterSum.y / clusterSvgCenters.length,
      };

      const count = clusterLabels.length;
      // Generate unique ID from first label's uuid and cluster index
      const clusterId = `cluster-${clusterLabels[0]?.pin?.uuid || clusters.length}`;
      clusters.push({
        id: clusterId,
        labels: clusterLabels,
        labelBounds: clusterLabelBounds,
        svgCenters: clusterSvgCenters,
        center: clusterSvgCenter, // SVG center for rendering
        pctCenter: clusterPctCenter, // Percentage center for internal use
        bounds: calculateClusterBounds(clusterLabelBounds),
        badgeBounds: estimateClusterBadgeBounds(
          clusterPctCenter, count, zoomScale, viewBox, containerSize,
        ),
        count,
      });
    }
  });
  profiler.end('  Clustering', clusteringStart);
  profiler.end('Phase 1: Initial clustering (spatial grid)', phase1Start);

  // Phase 2 and 3 removed: absorption and merging are predictive and cause
  // over-clustering. Let positioning (Phase 4) handle overlaps naturally.

  // Phase 4: Unprune — remove cluster members that don't cause collisions
  // After clustering defensively to prevent overlaps, greedily free members that
  // can render unclustered without colliding with anything. Handles cascading
  // removal: if member A collides with member B, check if B can also be removed;
  // if yes, then A becomes removable too. Repeat until stable (no more members
  // can be freed).
  const phase4Start = profiler.start('Phase 4: Unprune cluster members');
  let unpruned = true;
  const maxUnpruneIterations = 5;

  // Helper: recursively check if a member can be unclustered, accounting for
  // cascading removal of members it collides with.
  const canUncluswterWithCascade = (
    memberIdx, cluster, labelsWithBounds, visited = new Set(),
  ) => {
    if (visited.has(memberIdx)) return false; // Cycle detection
    visited.add(memberIdx);

    const memberBounds = cluster.labelBounds[memberIdx];
    const memberLabel = cluster.labels[memberIdx]?.pin?.label_text;

    // Find members that would collide if this one were removed
    const collidingIndices = [];
    cluster.labelBounds.forEach((bound, j) => {
      if (j !== memberIdx && boxesOverlap(memberBounds, bound)) {
        collidingIndices.push(j);
      }
    });

    // If collides with other members, check if they can also be unclustered
    if (collidingIndices.length > 0) {
      const allCollidersRemovable = collidingIndices.every(
        (j) => canUncluswterWithCascade(j, cluster, labelsWithBounds, visited),
      );
      if (!allCollidersRemovable) return false;
    }

    // Compute what the cluster would look like if this member AND all
    // cascading members were removed
    const removedSet = new Set(visited);
    const testBounds = cluster.labelBounds.filter((_, i) => !removedSet.has(i));
    const testCount = testBounds.length;

    // Check against visible unclustered labels
    const unclustered = labelsWithBounds.filter((l) => !l.clustered);
    if (unclustered.some((other) => boxesOverlap(memberBounds, other.bounds))) {
      // eslint-disable-next-line no-console
      console.log(`[Clustering] "${memberLabel}" blocked by unclustered label`);
      return false;
    }

    // Check against the smaller cluster's badge (if it would still exist)
    if (testCount > 0) {
      const testCenter = calculateClusterCenter(testBounds);
      const testBadgeBounds = estimateClusterBadgeBounds(
        testCenter, testCount, zoomScale, viewBox, containerSize,
      );
      if (boxesOverlap(memberBounds, testBadgeBounds)) {
        // eslint-disable-next-line no-console
        console.log(`[Clustering] "${memberLabel}" blocked by smaller cluster badge`);
        return false;
      }
    }

    // Check against other clusters' badges
    for (let c = 0; c < clusters.length; c += 1) {
      if (clusters[c] === cluster) continue;
      if (boxesOverlap(memberBounds, clusters[c].badgeBounds)) {
        // eslint-disable-next-line no-console
        console.log(`[Clustering] "${memberLabel}" blocked by other cluster badge`);
        return false;
      }
    }

    // Check if resulting cluster badge would overlap unclustered place labels
    if (testCount > 0) {
      const testCenter = calculateClusterCenter(testBounds);
      const testBadgeBounds = estimateClusterBadgeBounds(
        testCenter, testCount, zoomScale, viewBox, containerSize,
      );
      if (unclustered.some((other) => boxesOverlap(testBadgeBounds, other.bounds))) {
        // eslint-disable-next-line no-console
        console.log(`[Clustering] "${memberLabel}" blocked by badge overlap with unclustered label`);
        return false;
      }
    }

    // eslint-disable-next-line no-console
    console.log(`[Clustering] "${memberLabel}" can be unclustered`);
    return true;
  };

  for (let iteration = 0; iteration < maxUnpruneIterations && unpruned; iteration += 1) {
    unpruned = false;

    clusters.forEach((cluster) => {
      if (cluster.labels.length <= 1) return;

      // Collect indices to remove (using cascading check)
      const toRemove = new Set();

      for (let i = 0; i < cluster.labels.length; i += 1) {
        if (canUncluswterWithCascade(i, cluster, labelsWithBounds)) {
          toRemove.add(i);
          // Mark in labelsWithBounds as unclustered for subsequent checks
          const label = labelsWithBounds.find((l) => l.place === cluster.labels[i]);
          if (label) label.clustered = false;
          unpruned = true;
        }
      }

      // Rebuild cluster with remaining members
      if (toRemove.size === 0) return;
      if (toRemove.size === cluster.labels.length) {
        cluster.labels.length = 0;
        return;
      }

      const keepIndices = [];
      cluster.labels.forEach((_, i) => {
        if (!toRemove.has(i)) keepIndices.push(i);
      });

      const oldLabels = cluster.labels;
      const oldBounds = cluster.labelBounds;
      const oldCenters = cluster.svgCenters;

      cluster.labels = keepIndices.map((i) => oldLabels[i]);
      cluster.labelBounds = keepIndices.map((i) => oldBounds[i]);
      cluster.svgCenters = keepIndices.map((i) => oldCenters[i]);
      cluster.count = cluster.labels.length;

      // Recalculate cluster metadata
      cluster.pctCenter = calculateClusterCenter(cluster.labelBounds);
      cluster.bounds = calculateClusterBounds(cluster.labelBounds);

      const svgCenterSum = cluster.svgCenters.reduce(
        (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
        { x: 0, y: 0 },
      );
      cluster.center = {
        x: svgCenterSum.x / cluster.svgCenters.length,
        y: svgCenterSum.y / cluster.svgCenters.length,
      };

      cluster.badgeBounds = estimateClusterBadgeBounds(
        cluster.pctCenter, cluster.count, zoomScale, viewBox, containerSize,
      );
    });
  }

  // Remove empty clusters
  const nonEmptyClusters = clusters.filter((c) => c.labels.length > 0);
  clusters.length = 0;
  clusters.push(...nonEmptyClusters);

  profiler.end('Phase 4: Unprune cluster members', phase4Start);

  // Phase 5: Absorb unclustered place labels overlapped by cluster badges
  const phase5Start = profiler.start('Phase 5: Absorb overlapped place labels');
  clusters.forEach((cluster) => {
    const labelsToAbsorb = [];

    // Find unclustered labels that overlap with this cluster's badge
    labelsWithBounds.forEach((labelWithBounds) => {
      if (labelWithBounds.clustered) return; // Already clustered
      if (boxesOverlap(cluster.badgeBounds, labelWithBounds.bounds)) {
        labelsToAbsorb.push(labelWithBounds);
      }
    });

    // Absorb overlapping labels into the cluster
    if (labelsToAbsorb.length > 0) {
      labelsToAbsorb.forEach((absorbed) => {
        cluster.labels.push(absorbed.place);
        cluster.labelBounds.push(absorbed.bounds);
        cluster.svgCenters.push(absorbed.svgCenter);
        absorbed.clustered = true;
      });

      // Recalculate cluster metadata
      cluster.count = cluster.labels.length;
      cluster.pctCenter = calculateClusterCenter(cluster.labelBounds);
      cluster.bounds = calculateClusterBounds(cluster.labelBounds);

      const svgCenterSum = cluster.svgCenters.reduce(
        (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
        { x: 0, y: 0 },
      );
      cluster.center = {
        x: svgCenterSum.x / cluster.svgCenters.length,
        y: svgCenterSum.y / cluster.svgCenters.length,
      };

      cluster.badgeBounds = estimateClusterBadgeBounds(
        cluster.pctCenter, cluster.count, zoomScale, viewBox, containerSize,
      );
    }
  });

  profiler.end('Phase 5: Absorb overlapped place labels', phase5Start);

  // Phase 5b: Merge clusters with overlapping badges
  const phase5bStart = profiler.start('Phase 5b: Merge overlapping cluster badges');
  let merged = true;
  const maxMergeIterations = 10;
  let mergeIteration = 0;

  while (merged && mergeIteration < maxMergeIterations) {
    merged = false;
    mergeIteration += 1;

    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        const clusterI = clusters[i];
        const clusterJ = clusters[j];

        if (boxesOverlap(clusterI.badgeBounds, clusterJ.badgeBounds)) {
          // Merge clusterJ into clusterI
          clusterI.labels.push(...clusterJ.labels);
          clusterI.labelBounds.push(...clusterJ.labelBounds);
          clusterI.svgCenters.push(...clusterJ.svgCenters);

          // Recalculate clusterI metadata
          clusterI.count = clusterI.labels.length;
          clusterI.pctCenter = calculateClusterCenter(clusterI.labelBounds);
          clusterI.bounds = calculateClusterBounds(clusterI.labelBounds);

          const svgCenterSum = clusterI.svgCenters.reduce(
            (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
            { x: 0, y: 0 },
          );
          clusterI.center = {
            x: svgCenterSum.x / clusterI.svgCenters.length,
            y: svgCenterSum.y / clusterI.svgCenters.length,
          };

          clusterI.badgeBounds = estimateClusterBadgeBounds(
            clusterI.pctCenter, clusterI.count, zoomScale, viewBox, containerSize,
          );

          // Remove clusterJ
          clusters.splice(j, 1);
          merged = true;
          break;
        }
      }
      if (merged) break;
    }
  }

  profiler.end('Phase 5b: Merge overlapping cluster badges', phase5bStart);

  // Collect unclustered labels
  const unclustered = labelsWithBounds
    .filter((label) => !label.clustered)
    .map((label) => label.place);

  profiler.end('Total clustering', totalStart);

  if (enableProfiling) {
    // eslint-disable-next-line no-console
    console.log(`[Clustering] Result: ${clusters.length} clusters, ${unclustered.length} unclustered labels`);
  }

  // Include debug bounds data when debug mode is enabled
  const debugBounds = CLUSTERING_DEBUG ? labelsWithBounds.map((label) => ({
    uuid: label.place?.pin?.uuid,
    labelText: label.place?.pin?.label_text,
    orientation: label.place?.pin?.label_orientation || 'right',
    bounds: label.bounds,
    clustered: label.clustered,
  })) : null;

  // Include cluster badge bounds for debug visualization
  const debugClusterBounds = CLUSTERING_DEBUG ? clusters.map((cluster) => ({
    id: cluster.id,
    count: cluster.count,
    badgeBounds: cluster.badgeBounds,
    pctCenter: cluster.pctCenter,
  })) : null;

  return {
    clusters, unclustered, debugBounds, debugClusterBounds,
  };
};
